import {
	Background,
	Controls,
	type OnNodeDrag,
	ReactFlow,
	ReactFlowProvider,
	useEdgesState,
	useNodesState,
	useReactFlow,
} from "@xyflow/react";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";

import "@xyflow/react/dist/style.css";

import { ROOT_PARENT_ID } from "@/adapters/api";
import { Card } from "@/components/ui/card";
import { usePermissions } from "@/hooks/use-permissions.ts";
import { useUpdateStage } from "../../hooks/use-update-stage.ts";
import type { Stage } from "../../types.ts";
import { isStagesLockedError } from "../../utils/lock.ts";
import { descendantIds } from "../../utils/tree.ts";
import { buildFlow, type StageNodeType } from "./layout.ts";
import { StageNode } from "./stage-node.tsx";

const nodeTypes = { stage: StageNode };

interface StagesDiagramProps {
	constructionId: string;
	stages: Stage[];
	locked: boolean;
	stagePath: (stageId: string) => string;
}

function Diagram({
	constructionId,
	stages,
	locked,
	stagePath,
}: StagesDiagramProps) {
	const navigate = useNavigate();
	const { has } = usePermissions();
	const canUpdate = has("construction_stages:update") && !locked;
	const { mutate: mutateStage } = useUpdateStage(constructionId);
	const { getIntersectingNodes } = useReactFlow();

	const moveStage = useCallback(
		(stageId: string, parentStageId: string, onError?: () => void) => {
			mutateStage(
				{ stageId, input: { parentStageId } },
				{
					onSuccess: () => toast.success("Etapa movida com sucesso!"),
					onError: (err) => {
						if (!isStagesLockedError(err)) {
							toast.error("Houve um erro ao mover a etapa.");
						}
						onError?.();
					},
				},
			);
		},
		[mutateStage],
	);

	const flow = useMemo(() => {
		const { nodes, edges } = buildFlow(stages);
		return {
			edges,
			nodes: nodes.map((node) => ({
				...node,
				data: {
					...node.data,
					canPromote: canUpdate,
					onPromote: (stageId: string) => moveStage(stageId, ROOT_PARENT_ID),
				},
			})),
		};
	}, [stages, canUpdate, moveStage]);

	const [nodes, setNodes, onNodesChange] = useNodesState<StageNodeType>(
		flow.nodes,
	);
	const [edges, setEdges, onEdgesChange] = useEdgesState(flow.edges);

	useEffect(() => {
		setNodes(flow.nodes);
		setEdges(flow.edges);
	}, [flow, setNodes, setEdges]);

	const resetLayout = useCallback(() => {
		setNodes(flow.nodes);
	}, [flow, setNodes]);

	const handleNodeDragStop = useCallback<OnNodeDrag<StageNodeType>>(
		(_event, node) => {
			const target = getIntersectingNodes(node)[0];
			const stage = stages.find((item) => item.id === node.id);
			if (!target || !stage || target.id === stage.parentId) {
				resetLayout();
				return;
			}
			if (descendantIds(stages, node.id).has(target.id)) {
				toast.error(
					"Não é possível mover uma etapa para dentro de uma de suas sub-etapas.",
				);
				resetLayout();
				return;
			}
			moveStage(node.id, target.id, resetLayout);
		},
		[getIntersectingNodes, stages, resetLayout, moveStage],
	);

	return (
		<Card className="stages-diagram">
			<ReactFlow<StageNodeType>
				nodes={nodes}
				edges={edges}
				nodeTypes={nodeTypes}
				onNodesChange={onNodesChange}
				onEdgesChange={onEdgesChange}
				onNodeDragStop={handleNodeDragStop}
				onNodeClick={(_event, node) => {
					if (canUpdate) navigate(stagePath(node.id));
				}}
				nodesDraggable={canUpdate}
				nodesConnectable={false}
				fitView
				minZoom={0.2}
			>
				<Background />
				<Controls />
			</ReactFlow>
		</Card>
	);
}

export function StagesDiagram(props: StagesDiagramProps) {
	return (
		<ReactFlowProvider>
			<Diagram {...props} />
		</ReactFlowProvider>
	);
}
